import { getSportsnewBetErrorCode, getSportsnewBetErrorMessage } from './sportsnewBetErrorMessage.js';
import {
  extractSportsnewAuthoritativeRequoteUpdates,
  SPORTSNEW_SELECTED_RECOVERY_CODES,
  sportsnewPreviewFailureDetails,
  sportsnewPreviewFailurePayload,
  sportsnewPreviewLatestOdds,
  stableSportsnewLatestOddsRevision,
} from './sportsnewAuthoritativeRequote.js';

export {
  extractSportsnewAuthoritativeRequoteUpdates,
  SPORTSNEW_SELECTED_RECOVERY_CODES,
};

export const shouldResyncAfterBetError = (message = '') => {
  const raw = String(message || '');
  const text = raw.replace(/\s+/g, '').toLowerCase();
  if (!text) return false;

  const hasBettingSubject =
    text.includes('배당')
    || text.includes('마켓')
    || text.includes('경기')
    || text.includes('선택')
    || /odd|market|selection|fixture|match/.test(text);

  const hasChangedState =
    text.includes('변경')
    || text.includes('변동')
    || text.includes('마감')
    || text.includes('잠김')
    || text.includes('잠겼')
    || text.includes('중지')
    || text.includes('종료')
    || text.includes('최신')
    || text.includes('닫')
    || /chang|clos|suspend|lock|unavailable|expired|stale/.test(text);

  return hasBettingSubject && hasChangedState;
};

const firstDefined = (...values) => values.find(
  (value) => value !== undefined && value !== null && value !== '',
);

const previewFailureHttpStatus = (source) => {
  const value = firstDefined(
    source?.response?.status,
    source?.httpStatus,
    source?.http_status,
    source?.statusCode,
    source?.status_code,
    typeof source?.status === 'number' ? source.status : undefined,
  );
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
};

const PREVIEW_NETWORK_ERROR_CODES = new Set([
  'ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ECONNRESET', 'ENETUNREACH',
]);

const isSportsnewRecoveryNetworkFailure = (source) => {
  if (!source || typeof source !== 'object') return false;
  if (source.networkError === true || source.isNetworkError === true) return true;
  if (PREVIEW_NETWORK_ERROR_CODES.has(String(source.code || '').toUpperCase())) return true;
  if (source.request && !source.response) return true;
  return !source.response && /network\s*error|failed\s*to\s*fetch|timed?\s*out/i.test(String(source.message || ''));
};

const AUTHORITATIVE_TERMINAL_BET_STATUSES = new Set([
  'REJECTED', 'VOID', 'VOIDED', 'CANCELLED', 'CANCELED',
]);

/**
 * Shared fail-closed eligibility for preview and direct recovery. Structured
 * selected-state errors are recoverable, as is an explicitly forced terminal
 * bet result. Auth, transport, policy, unknown and message-only failures are
 * never allowed to mutate selected cart state.
 */
export const isSportsnewSelectedRecoveryEligible = ({
  source,
  allowAuthoritativeTerminal = false,
} = {}) => {
  if (!source || typeof source !== 'object') return false;
  if (isSportsnewRecoveryNetworkFailure(source)) return false;
  const httpStatus = previewFailureHttpStatus(source);
  if (httpStatus === 401 || httpStatus === 403) return false;

  const code = getSportsnewBetErrorCode(source);
  if (code) return SPORTSNEW_SELECTED_RECOVERY_CODES.has(code);
  if (!allowAuthoritativeTerminal) return false;

  const payload = sportsnewPreviewFailurePayload(source);
  const nested = payload?.data && typeof payload.data === 'object' ? payload.data : {};
  const terminalStatus = String(firstDefined(
    nested.status,
    payload.status,
    source.status,
  ) || '').trim().toUpperCase();
  return AUTHORITATIVE_TERMINAL_BET_STATUSES.has(terminalStatus);
};

export const shouldRecoverSportsnewSelectedOdds = (source) => (
  isSportsnewSelectedRecoveryEligible({ source })
);

/**
 * Produce a semantic one-shot key only for authoritative preview failures that
 * are safe to repair through the existing selected-odds REST recovery path.
 * Transport/auth/policy failures intentionally produce no key.
 */
export const buildSportsnewPreviewRecoveryKey = ({ previewKey, source } = {}) => {
  if (!previewKey) return '';
  if (!isSportsnewSelectedRecoveryEligible({ source })) return '';

  const code = getSportsnewBetErrorCode(source);

  const payload = sportsnewPreviewFailurePayload(source);
  const details = sportsnewPreviewFailureDetails(payload);
  const latestOdds = sportsnewPreviewLatestOdds(payload, details);

  return JSON.stringify({
    previewKey,
    code,
    revision: {
      oddStateId: firstDefined(details.oddStateId, details.odd_state_id),
      currentPriceDecimal: firstDefined(
        details.currentPriceDecimal,
        details.current_price_decimal,
      ),
      state: firstDefined(details.state, details.displayState, details.display_state),
      bettable: firstDefined(details.bettable, details.isBettable, details.is_bettable),
      sourceReceivedAt: firstDefined(
        details.sourceReceivedAt,
        details.source_received_at,
      ),
      updatedAt: firstDefined(details.updatedAt, details.updated_at),
      sourceEventId: firstDefined(details.sourceEventId, details.source_event_id),
      version: firstDefined(details.version, details.sourceVersion, details.source_version),
      latestOdds: stableSportsnewLatestOddsRevision(latestOdds),
    },
  });
};

export const normalizeSportsnewPreviewFailure = ({
  previewKey,
  source,
  fallbackCode,
} = {}) => ({
  source,
  previewKey,
  code: getSportsnewBetErrorCode(source) || fallbackCode || 'PREVIEW_REJECTED',
  recoveryKey: buildSportsnewPreviewRecoveryKey({ previewKey, source }),
});

export const claimSportsnewPreviewRecoveryKey = ({
  processedRecoveryKeys,
  recoveryKey,
  limit = 32,
} = {}) => {
  if (!(processedRecoveryKeys instanceof Set) || !recoveryKey || processedRecoveryKeys.has(recoveryKey)) {
    return false;
  }
  const boundedLimit = Math.max(1, Math.min(128, Math.trunc(Number(limit)) || 32));
  while (processedRecoveryKeys.size >= boundedLimit) {
    const oldestKey = processedRecoveryKeys.values().next().value;
    processedRecoveryKeys.delete(oldestKey);
  }
  processedRecoveryKeys.add(recoveryKey);
  return true;
};

export const releaseSportsnewPreviewRecoveryKeyForRetry = ({
  processedRecoveryKeys,
  recoveryAttempts,
  recoveryKey,
  maxAttempts = 2,
  limit = 32,
} = {}) => {
  if (
    !(processedRecoveryKeys instanceof Set)
    || !(recoveryAttempts instanceof Map)
    || !recoveryKey
  ) return { attempts: 0, shouldRetry: false };

  const boundedLimit = Math.max(1, Math.min(128, Math.trunc(Number(limit)) || 32));
  if (!recoveryAttempts.has(recoveryKey)) {
    while (recoveryAttempts.size >= boundedLimit) {
      const oldestKey = recoveryAttempts.keys().next().value;
      recoveryAttempts.delete(oldestKey);
    }
  }
  const attempts = (recoveryAttempts.get(recoveryKey) || 0) + 1;
  recoveryAttempts.set(recoveryKey, attempts);
  const boundedMaxAttempts = Math.max(1, Math.min(5, Math.trunc(Number(maxAttempts)) || 2));
  const shouldRetry = attempts < boundedMaxAttempts;
  if (shouldRetry) processedRecoveryKeys.delete(recoveryKey);
  else processedRecoveryKeys.add(recoveryKey);
  return { attempts, shouldRetry };
};

export const buildSportsnewPreviewRecoveryClaimKey = ({ accountKey, recoveryKey } = {}) => {
  const owner = String(accountKey || '');
  const recovery = String(recoveryKey || '');
  return owner && recovery ? JSON.stringify([owner, recovery]) : '';
};

export const releaseSportsnewPreviewRecoveryKey = ({ processedRecoveryKeys, recoveryKey } = {}) => (
  processedRecoveryKeys instanceof Set
  && typeof recoveryKey === 'string'
  && recoveryKey.length > 0
  && processedRecoveryKeys.delete(recoveryKey)
);

export const recoverAfterSportsnewBetError = async ({
  source,
  submittedSelections,
  displayScope,
  recoverSelectedOddsForCart,
  forceResyncOdds,
  forceSelectedRecovery = false,
  isCancelled = () => false,
  recoveryOwnerKey = '',
}) => {
  if (isCancelled()) return { path: 'cancelled' };
  if (isSportsnewSelectedRecoveryEligible({
    source,
    allowAuthoritativeTerminal: forceSelectedRecovery === true,
  })) {
    const authoritativeUpdates = extractSportsnewAuthoritativeRequoteUpdates(
      source,
      submittedSelections,
    );
    let recovery = null;
    if (typeof recoverSelectedOddsForCart === 'function') {
      recovery = await recoverSelectedOddsForCart(submittedSelections, displayScope, {
        authoritativeUpdates,
        isCancelled,
        recoveryOwnerKey,
      });
    }
    if (isCancelled()) return { path: 'cancelled' };
    if (recovery?.applied === false && recovery?.recovered !== true) {
      return {
        path: 'sportsnew_selected_failed',
        reason: recovery.reason || 'request_failed',
      };
    }
    return {
      path: 'sportsnew_selected',
      recovered: recovery?.recovered === true,
      authoritativeUpdateCount: authoritativeUpdates.length,
      recovery,
    };
  }

  const message = getSportsnewBetErrorMessage(source, '')
    || source?.void_reason
    || source?.voidReason
    || source?.message;
  if (shouldResyncAfterBetError(message) && typeof forceResyncOdds === 'function') {
    await forceResyncOdds(submittedSelections, { isCancelled, recoveryOwnerKey });
    return { path: isCancelled() ? 'cancelled' : 'legacy_force' };
  }
  return { path: 'none' };
};

export const recoverAfterSportsnewBonusConfirmError = ({
  bonusRecoveryRequest,
  fallbackSelections,
  ...recovery
}) => recoverAfterSportsnewBetError({
  ...recovery,
  submittedSelections: Array.isArray(bonusRecoveryRequest?.submittedSelections)
    ? bonusRecoveryRequest.submittedSelections
    : fallbackSelections,
});
